import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Divider, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import ByzMuiButton from 'src/components/core-components/mui/buttons/ByzMuiButton';
import ByzMuiPrintButton from 'src/components/core-components/mui/buttons/ByzMuiPrintButton';
import ByzMuiAdButtonDogrula from 'src/components/core-components/mui/buttons/ByzMuiAdButtonDogrula';
import { useTranslate } from 'src/locales';
import { nurseService } from 'src/api/nurse/nurseService';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import { ReactComponent as OrderIcon } from '../assets/order-icon.svg';

import { ReactComponent as SandClockIcon } from '../assets/gg_sand-clock.svg';
import { ReactComponent as ReturnIcon } from '../assets/icon-park-outline_return.svg';

interface MenuProps {
  selectedTab?: string;
  onTabChange?: (tab: string) => void;
  patientName?: string;
  srnids?: number[];
  srumids?: number[];
  isIdentityVerified?: boolean;
  onRefetch?: () => void;
}

const Menu: React.FC<MenuProps> = ({
  selectedTab = 'order',
  onTabChange,
  patientName,
  srnids = [],
  srumids = [],
  isIdentityVerified = false,
  onRefetch,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTab, setCurrentTab] = useState(selectedTab);
  const [isVerifying, setIsVerifying] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'success',
  });

  const showSnackbar = (message: string, type: 'success' | 'error' = 'success', title?: string) => {
    setSnackbar({
      open: true,
      message,
      type,
      title: title || (type === 'success' ? t('success') : t('error')),
    });
  };

  useEffect(() => {
    setCurrentTab(selectedTab);
  }, [selectedTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  const handleIdentityVerification = async () => {
    if (srnids.length === 0 && srumids.length === 0) {
      showSnackbar('Kimlik doğrulama için geçerli veri bulunamadı.', 'error');
      return;
    }

    setIsVerifying(true);
    try {
      await nurseService.order.authenForPatient({
        srnids,
        srumids,
      });
      showSnackbar('Kimlik doğrulama başarılı.', 'success');
      onRefetch?.();
    } catch (error) {
      console.error('Kimlik doğrulama hatası:', error);
      showSnackbar('Kimlik doğrulama sırasında bir hata oluştu.', 'error');
    } finally {
      setIsVerifying(false);
    }
  };


  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: { xs: '12px', sm: '16px', md: '20px' },
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        px: { xs: 1, sm: 1.5, md: 2.5 },
        py: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 0 },
          py: 0,
        }}
      >
        {/* Tabs */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2.5 },
            width: { xs: '100%', md: 'auto' },
            overflowX: { xs: 'auto', md: 'visible' },
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={false}
            sx={{
              minHeight: { xs: '56px', sm: '60px', md: '68px' },
              width: { xs: '100%', md: 'auto' },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: { xs: '56px', sm: '60px', md: '68px' },
                padding: { xs: '16px 8px', sm: '20px 12px', md: '24px 0' },
                textTransform: 'none',
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                lineHeight: '140%',
                color: '#687787',
                minWidth: { xs: 'auto', md: 'auto' },
                '&.Mui-selected': {
                  color: '#2979C3',
                },
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75, md: 1 } }}>
                  <OrderIcon width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} aria-hidden />
                  <span>{t('nurse.order-medicine.tabs.order')}</span>
                </Box>
              }
              value="order"
              sx={{
                borderBottom: currentTab === 'order' ? '2px solid #2979C3' : 'none',
              }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75, md: 1 } }}>
                  <SandClockIcon width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} aria-hidden />
                  <span>{t('nurse.order-medicine.tabs.pending')}</span>
                </Box>
              }
              value="pending"
              sx={{
                borderBottom: currentTab === 'pending' ? '2px solid #2979C3' : 'none',
              }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75, md: 1 } }}>
                  <ReturnIcon width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} aria-hidden />
                  <span>{t('nurse.order-medicine.tabs.return')}</span>
                </Box>
              }
              value="return"
              sx={{
                borderBottom: currentTab === 'return' ? '2px solid #2979C3' : 'none',
              }}
            />
          </Tabs>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5, md: 2.5 },
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          {/* Kimlik Doğrulama Button */}
          {isIdentityVerified ? (
            <Box sx={{ minWidth: { xs: 'auto', sm: '140px', md: '157px' }, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}>
              <ByzMuiAdButtonDogrula patientName={patientName} />
            </Box>
          ) : (
            <ByzMuiButton
              onClick={handleIdentityVerification}
              disabled={isVerifying || (srnids.length === 0 && srumids.length === 0)}
              sx={{
                borderRadius: { xs: '12px', sm: '16px', md: '20px' },
                backgroundColor: 'rgba(99, 180, 255, 0.2)',
                border: '1px solid #2979C3',
                color: '#2979C3',
                fontSize: { xs: '11px', sm: '12px', md: '13px' },
                px: { xs: 1, sm: 1.125, md: 1.25 },
                py: { xs: 0.375, sm: 0.4375, md: 0.5 },
                minWidth: { xs: 'auto', sm: '140px', md: '157px' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'rgba(99, 180, 255, 0.3)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(99, 180, 255, 0.1)',
                  borderColor: '#a0c4e8',
                  color: '#a0c4e8',
                },
              }}
            >
              {isVerifying ? (
                <CircularProgress size={18} sx={{ color: '#2979C3' }} />
              ) : (
                t('nurse.order-medicine.buttons.identity-verification')
              )}
            </ByzMuiButton>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: { xs: '40px', sm: '45px', md: '51px' },
              borderColor: 'rgba(0, 0, 0, 0.5)',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          {/* Print Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 1.875 } }}>
            <ByzMuiPrintButton
              type={1}
              disabled
              sx={{
                width: { xs: '40px', sm: '45px', md: '50px' },
                height: { xs: '40px', sm: '45px', md: '50px' }
              }}
            />
            <ByzMuiPrintButton
              type={2}
              disabled
              sx={{
                width: { xs: '40px', sm: '45px', md: '50px' },
                height: { xs: '40px', sm: '45px', md: '50px' }
              }}
            />
          </Box>
        </Box>
      </Box>

      <ByzMuiSnackBar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        title={snackbar.title}
        message={snackbar.message}
        type={snackbar.type}
      />
    </Box>
  );
};

export default Menu;
